import React, { useState, useEffect } from "react";
import { Input, Button, message } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { processPayment, PaymentPayload } from "../apis/payment";
import Modal from "../../src/components/Modal"; 

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  total: number;
  discount?: number;
  patientName?: string;
  patientId?: number;
  sourceType: "visit" | "pathology" | "pharmacy";
  sourceId?: number;
  /** When true, allows partial payment or unpaid (balance can be negative) */
  allowPartialOrUnpaid?: boolean;
  testNames?: string[];
  selectedOrderIds?: number[];
  orderAmounts?: Record<number, number>; // Map of orderId -> amount
  advanceBalance?: number;
  onSave?: (paymentData: PaymentData) => void;
  onPrint?: () => void;
  onPaymentSuccess?: () => void;
}

export interface PaymentData {
  upi: number;
  cash: number;
  card: number;
  cheque: number;
  online: number;
  paymentNote: string;
  advanceUsed?: number;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  total,
  discount = 0,
  patientId = 0,
  sourceType,
  sourceId,
  allowPartialOrUnpaid = false,
  testNames = [],
  patientName = "Counter Sale",
  selectedOrderIds = [],
  orderAmounts = {},
  advanceBalance,
  onSave,
  onPrint,
  onPaymentSuccess,
}) => {
  const [payments, setPayments] = useState<PaymentData>({
    upi: 0,
    cash: 0,
    card: 0,
    cheque: 0,
    online: 0,
    paymentNote: "",
  });
  const [advanceUsed, setAdvanceUsed] = useState(0);

  const calculateRoundOff = (amount: number) => {
    const rounded = Math.floor(amount);
    return +(rounded - amount).toFixed(2);
  };

  const grossAmount = total - discount;
  const calculatedRoundOff = calculateRoundOff(grossAmount);
  const amountDue = grossAmount + calculatedRoundOff;

  useEffect(() => {
    if (isOpen) {
      setPayments({
        upi: 0,
        cash: 0,
        card: 0,
        cheque: 0,
        online: 0,
        paymentNote: "",
      });
      setAdvanceUsed(0);
    }
  }, [isOpen, amountDue]);

  const amountDueAfterAdvance = Math.max(amountDue - advanceUsed, 0);

  const handlePaymentChange = (
    field: keyof PaymentData,
    value: number | string
  ) => {
    if (field === "paymentNote") {
      setPayments((prev) => ({ ...prev, paymentNote: value as string }));
      return;
    }
    const val = typeof value === "number" ? value : 0;

    setPayments((prev) => {
      const updated = { ...prev, [field]: val };
      // When allowPartialOrUnpaid, don't auto-fill UPI - let user enter amounts freely
      if (!allowPartialOrUnpaid && field !== "upi") {
        const otherTotal =
          (updated.cash || 0) +
          (updated.card || 0) +
          (updated.cheque || 0) +
          (updated.online || 0);
        updated.upi = Math.max(amountDueAfterAdvance - otherTotal, 0);
      }
      return updated;
    });
  };

  const handleAdvanceChange = (value: number | string) => {
    const raw = typeof value === "number" ? value : 0;
    const maxAdvance = advanceBalance || 0;
    const nextAdvance = Math.min(Math.max(raw, 0), maxAdvance);
    setAdvanceUsed(nextAdvance);
    if (!allowPartialOrUnpaid) {
      const nextDue = Math.max(amountDue - nextAdvance, 0);
      setPayments((prev) => {
        const otherTotal =
          (prev.cash || 0) +
          (prev.card || 0) +
          (prev.cheque || 0) +
          (prev.online || 0);
        return {
          ...prev,
          upi: Math.max(nextDue - otherTotal, 0),
        };
      });
    }
  };

  const calculatePaying = () => {
    return (
      (payments.upi || 0) +
      (payments.cash || 0) +
      (payments.card || 0) +
      (payments.cheque || 0) +
      (payments.online || 0)
    );
  };

  const paying = calculatePaying();
  const balance = paying - amountDueAfterAdvance;

  const handleSave = async () => {
    if (!allowPartialOrUnpaid && balance < 0) {
      message.error("Paying amount should not be less than amount due");
      return;
    }

    // If onSave callback is provided for custom handling
    if (onSave) {
      onSave({ ...payments, advanceUsed });
      return;
    }

    try {
      // Build payment modes array (only include non-zero amounts)
      const paymentModes: Array<{
        amount: number;
        payment_mode: string;
        payment_date: string;
        notes?: string;
        allocation_type: "manual";
      }> = [];

      const currentDate = new Date().toISOString().split("T")[0];

      if (payments.cash > 0) {
        paymentModes.push({
          amount: payments.cash,
          payment_mode: "cash",
          payment_date: currentDate,
          notes: payments.paymentNote || undefined,
          allocation_type: "manual",
        });
      }
      if (payments.upi > 0) {
        paymentModes.push({
          amount: payments.upi,
          payment_mode: "upi",
          payment_date: currentDate,
          notes: payments.paymentNote || undefined,
          allocation_type: "manual",
        });
      }
      if (payments.card > 0) {
        paymentModes.push({
          amount: payments.card,
          payment_mode: "card",
          payment_date: currentDate,
          notes: payments.paymentNote || undefined,
          allocation_type: "manual",
        });
      }
      if (payments.cheque > 0) {
        paymentModes.push({
          amount: payments.cheque,
          payment_mode: "cheque",
          payment_date: currentDate,
          notes: payments.paymentNote || undefined,
          allocation_type: "manual",
        });
      }
      if (payments.online > 0) {
        paymentModes.push({
          amount: payments.online,
          payment_mode: "online",
          payment_date: currentDate,
          notes: payments.paymentNote || undefined,
          allocation_type: "manual",
        });
      }
      if (advanceUsed > 0) {
        paymentModes.push({
          amount: advanceUsed,
          payment_mode: "cash",
          payment_date: currentDate,
          notes: payments.paymentNote || "Advance allocation",
          allocation_type: "manual",
        });
      }

      // Determine source IDs (either multiple selected orders or single source)
      const sourceIds =
        selectedOrderIds && selectedOrderIds.length > 0
          ? selectedOrderIds
          : sourceId
          ? [sourceId]
          : [];

      // if (sourceIds.length === 0) {
      //   message.error("No orders selected for payment");
      //   return;
      // }
      if (sourceIds.length === 0 && sourceType !== "pathology") {
        message.error("No orders selected for payment");
        return;
      }

      // Map sourceType to match backend expectations
      const mappedSourceType =
        sourceType === "pathology" ? "lab_order" : sourceType;

      // Build the payload according to new backend format
      const payload: PaymentPayload = {
        source_type: mappedSourceType as
          | "lab_order"
          | "pharmacy_sale"
          | "visit",
        source_ids: sourceIds,
        payments: paymentModes,
        invoice_date: currentDate,
      };

      // If multiple orders, include source_amounts with actual order amounts
      if (selectedOrderIds && selectedOrderIds.length > 1) {
        payload.source_amounts = {};
        selectedOrderIds.forEach((orderId) => {
          // Use actual order amount from orderAmounts prop, or calculate from total
          payload.source_amounts![orderId] = orderAmounts[orderId] || 0;
        });
      } else if (
        selectedOrderIds &&
        selectedOrderIds.length === 1 &&
        orderAmounts[selectedOrderIds[0]]
      ) {
        // Single order - include source_amounts for consistency
        payload.source_amounts = {
          [selectedOrderIds[0]]: orderAmounts[selectedOrderIds[0]],
        };
      }

      console.log("Payment Payload:", payload);

      // Process payment
      const response = await processPayment(payload);
      console.log("Payment Response:", response);

      message.success("Payment processed successfully");

      // Trigger success callback to refresh data
      if (onPaymentSuccess) {
        onPaymentSuccess();
      }

      onClose();
    } catch (err: any) {
      console.error("Payment Error:", err);
      message.error(err?.message || "Payment failed. Please try again!");
    }
  };

  if (!isOpen) return null;

  return (
    // <div
    //   className="fixed inset-0 z-50 overflow-y-auto"
    //   aria-labelledby="modal-title"
    //   role="dialog"
    //   aria-modal="true"
    // >
     <Modal
    title="Payment Details"
    width="sm:max-w-2xl"
    onClose={onClose}
    modalContent={
      <>
        {/* Backdrop */}
        {/* <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          aria-hidden="true"
          onClick={onClose}
        ></div> */}

        {/* Modal */}
        {/* <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:align-middle sm:max-w-2xl sm:w-full"> */}
          {/* Header */}
          {/* <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">
              Payment Details
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <CloseOutlined className="text-xl" />
            </button>
          </div> */}

          {/* Selected Tests */}
          {testNames.length > 0 && (
            <div className="p-4 bg-white">
              <div className="text-sm font-semibold text-gray-900 mb-2">
                Selected Tests -
              </div>
              <div className="flex flex-wrap gap-2">
                {testNames.map((name, index) => {
                  const orderId = selectedOrderIds[index];
                  const orderAmount = orderId ? orderAmounts[orderId] : 0;

                  return (
                    <div
                      key={index}
                      className="px-3 py-2 text-sm bg-white border rounded-md text-gray-800 shadow-sm"
                    >
                      <div className="font-medium">{name}</div>
                      {orderAmount > 0 && (
                        <div className="text-xs text-gray-600 mt-1">
                          ₹{orderAmount.toFixed(2)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="px-6 py-4">
            {/* Sale Summary */}
            <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
              <div className="grid grid-cols-5 gap-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Total:</div>
                  <div className="text-lg font-semibold text-blue-600">
                    ₹{total.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Discount:</div>
                  <div className="text-lg font-semibold text-orange-600">
                    ₹{discount.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Round Off:</div>
                  <div className="text-lg font-semibold text-gray-900">
                    ₹{calculatedRoundOff.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Amount Due:</div>
                  <div className="text-lg font-semibold text-green-600">
                    ₹{amountDueAfterAdvance.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Patient:</div>
                  <div className="text-md font-semibold text-gray-700">
                    {patientName}
                  </div>
                </div>
              </div>
              {typeof advanceBalance === "number" && (
                <div className="mt-4 grid grid-cols-5 gap-4 items-end">
                  <div className="col-span-2">
                    <div className="text-sm text-gray-600 mb-1">Advance Available:</div>
                    <div className="text-lg font-semibold text-green-700">
                      ₹{advanceBalance.toFixed(2)}
                    </div>
                  </div>
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Use Advance
                    </label>
                    <Input
                      type="number"
                      min={0}
                      max={advanceBalance}
                      value={advanceUsed || ""}
                      onChange={(e) =>
                        handleAdvanceChange(parseFloat(e.target.value) || 0)
                      }
                      className="block w-full mt-1 border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm"
                      placeholder="0"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Payment Details */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Payment Details
              </h3>
              <div className="grid grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    UPI
                  </label>
                  <Input
                    type="number"
                    value={payments.upi || ""}
                    onChange={(e) =>
                      handlePaymentChange(
                        "upi",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cash
                  </label>
                  <Input
                    type="number"
                    value={payments.cash || ""}
                    onChange={(e) =>
                      handlePaymentChange(
                        "cash",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Card
                  </label>
                  <Input
                    type="number"
                    value={payments.card || ""}
                    onChange={(e) =>
                      handlePaymentChange(
                        "card",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cheque
                  </label>
                  <Input
                    type="number"
                    value={payments.cheque || ""}
                    onChange={(e) =>
                      handlePaymentChange(
                        "cheque",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Online
                  </label>
                  <Input
                    type="number"
                    value={payments.online || ""}
                    onChange={(e) =>
                      handlePaymentChange(
                        "online",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    className="block w-full mt-1 border-gray-300 rounded-md shadow-sm text-gray-700 sm:text-sm"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div className="mb-6 flex items-center gap-10">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  Paying:
                </span>
                <span className="text-lg font-semibold text-green-600">
                  ₹{paying.toFixed(2)}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {allowPartialOrUnpaid ? "Balance (remaining):" : "Balance:"}
                </span>
                <span
                  className={`text-lg font-semibold ${
                    balance >= 0 ? "text-green-600" : "text-orange-600"
                  }`}
                >
                  ₹{balance.toFixed(2)}
                  {allowPartialOrUnpaid && balance < 0 && (
                    <span className="text-sm font-normal text-gray-500 ml-1">
                      (unpaid)
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Payment Note */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Note
              </label>
              <Input.TextArea
                rows={3}
                value={payments.paymentNote}
                onChange={(e) =>
                  handlePaymentChange("paymentNote", e.target.value)
                }
                placeholder="Enter any additional payment notes..."
                className="w-full"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button 
                onClick={handleSave}
                disabled={!allowPartialOrUnpaid && balance < 0}
                className="px-4 py-2 text-sm font-semibold text-white bg-primary rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>
      </>
    }
  />
  );
};

export default PaymentModal;
